export type TooltipPosition = {
  placement: "above" | "below" | "left" | "right" | "viewport-edge";
  x: number;
  y: number;
};

type Rect = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

type Size = {
  height: number;
  width: number;
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function getViewportAwareTooltipPosition({
  anchor,
  chart,
  gap = 16,
  margin = 12,
  tooltip,
  viewport,
}: {
  anchor: { x: number; y: number };
  chart: Rect;
  gap?: number;
  margin?: number;
  tooltip: Size;
  viewport: Size;
}): TooltipPosition {
  const centeredY = clamp(
    anchor.y - tooltip.height / 2,
    margin,
    viewport.height - margin - tooltip.height,
  );
  const centeredX = clamp(
    anchor.x - tooltip.width / 2,
    margin,
    viewport.width - margin - tooltip.width,
  );

  if (chart.left - margin >= tooltip.width + gap) {
    return {
      placement: "left",
      x: chart.left - gap - tooltip.width,
      y: centeredY,
    };
  }

  if (viewport.width - chart.right - margin >= tooltip.width + gap) {
    return {
      placement: "right",
      x: chart.right + gap,
      y: centeredY,
    };
  }

  if (chart.top - margin >= tooltip.height + gap) {
    return {
      placement: "above",
      x: centeredX,
      y: chart.top - gap - tooltip.height,
    };
  }

  if (viewport.height - chart.bottom - margin >= tooltip.height + gap) {
    return {
      placement: "below",
      x: centeredX,
      y: chart.bottom + gap,
    };
  }

  const leftEdgeX = margin;
  const rightEdgeX = Math.max(margin, viewport.width - margin - tooltip.width);
  const leftDistance = Math.abs(anchor.x - (leftEdgeX + tooltip.width / 2));
  const rightDistance = Math.abs(anchor.x - (rightEdgeX + tooltip.width / 2));

  return {
    placement: "viewport-edge",
    x: leftDistance >= rightDistance ? leftEdgeX : rightEdgeX,
    y: centeredY,
  };
}
